import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {Label, Modal, ModalBody, ModalHeader} from "reactstrap";
import {useAuth} from "../../../providers/AuthProvider";
import User from "../../../models/User";
import {useMutation} from '@tanstack/react-query';
import {addNoteForMeeting} from "../../../models/Meeting";
import FiveStarRating from "../../FiveStarRating";

const RatingFormInput = (props) => {
    const [hoverValue, setHoverValue] = useState(null);

    let inputValue = props.value || false

    return (
        <div className={'form-group ' + (props.className || '')}>
            {props.label !== false && (
                <Label className="col-form-label">
                    {props.label}
                </Label>
            )}

            <input type="hidden" name={props.name} value={inputValue || ''}/>

            <div className="star-form-group" onMouseLeave={() => {
                setHoverValue(null)
            }}>
                {TB.map([1, 2, 3, 4, 5], (value) => (
                    <div key={TB.slugify(props.name + "-rating-" + value)}
                         onClick={() => {
                             setHoverValue(value)
                             props.onChange(value)
                         }}
                         onMouseEnter={() => {
                             if (!inputValue || parseInt(inputValue) <= value) {
                                 setHoverValue(value)
                             }
                         }}
                         className={TB.mapClass("star-input", {
                             disabled: props.disabled,
                             active: parseInt(inputValue) === parseInt(value),
                         })
                         }>
                        <i className={TB.mapClass({
                            "fas fa-star": parseInt(hoverValue || inputValue || 0) >= parseInt(value),
                            "far fa-star": parseInt(hoverValue || inputValue || 0) < parseInt(value)
                        })}/>

                    </div>
                ))}

            </div>

        </div>
    )
};

const MeetingRatingsModal = ({meeting, onToggle, isOpen,...props}) => {

    const {auth} = useAuth();

    const avgRating = meeting.averageRating();
    const myRating = meeting.ratingForMember(auth?.id);

    const [isEditing, setIsEditing] = useState(false);
    const [noteModel, setNoteModel] = useState({content: '', additional_data: {}});

    const submitNoteForMeeting = useMutation(['submitNoteMutation'], () => addNoteForMeeting(
        props.position?.id,
        meeting?.id,
        noteModel
    ), {
            onSuccess: async (note) => {
                await setIsEditing(false);

                await TB.toast("Rating Submitted", {type: 'success'});

                await props.onUpdate({notes: TB.orderBy(TB.uniqBy([note, ...meeting.notes], 'id'), 'created_at')});
                await setNoteModel({content: '', additional_data: {}})
            },
            onError: async (e) => {

                console.log('onError', e)
                await TB.showServerErrorToast();
            }
        })

    return (
        <Modal isOpen={isOpen} className="meeting-comments" size="lg">
            <ModalHeader toggle={() => onToggle()}>
                <div className="flex flex-col">
                    <span>Ratings/Notes For <strong>{meeting.name}</strong></span>
                    <small>
                        <i className="far fa-calendar-alt mr-1"/>
                        <span>
                            {meeting.acceptedTime()}
                        </span>
                    </small>
                </div>
            </ModalHeader>
            <ModalBody>
                <h5 className="text-warning">
                    <FiveStarRating rating={avgRating}/>
                    &nbsp;&nbsp;
                    <small>
                        <strong className="text-black">
                            {Number(avgRating).toFixed(1)}
                        </strong> out of {TB.pluralize("Reviews", meeting.notes?.length, true)}
                    </small>
                </h5>
                <div className="comment-widgets m-b-2">

                    {TB.map(meeting.notes || [], note => {
                        if (!note.creator) return null;
                        let creator = User.make(note.creator);

                        let rating = TB.get(note, 'additional_data.rating');

                        if (rating) rating = parseFloat(rating);

                        const createdByCurrentUser = note.creator?.id === auth?.id;
                        return (
                            <div className="flex flex-row comment-row" key={`note-${note.id}`}>
                                <div className="p-2">
                                    <span className="round">
                                        <img src={creator.image_path} alt=""/>
                                    </span>
                                </div>
                                <div className="comment-text w-100">
                                    <h5>{creator?.fullName()}</h5>
                                    {(!isEditing || !createdByCurrentUser) && (
                                        <React.Fragment>
                                            <div className="comment-footer">
                                                <span className="date">
                                                    {TB.moment(note.created_at).fromNow()}
                                                </span>
                                                {rating && (
                                                    <FiveStarRating
                                                        key={`${meeting.id}-note-${note.id}`}
                                                        rating={rating}
                                                        className={createdByCurrentUser ? "my-rating" : undefined}
                                                    />
                                                )}
                                                {createdByCurrentUser && (
                                                    <button onClick={() => {
                                                        setNoteModel({...note})
                                                        setIsEditing(true)
                                                    }} className="btn btn-link py-0 btn-link-primary mx-2">
                                                        <i className="fa fa-edit mr-1"/>
                                                        Edit
                                                    </button>
                                                )}
                                            </div>
                                            <p className="m-b-5 m-t-10">
                                                {note.content}
                                            </p>
                                        </React.Fragment>
                                    )}

                                    {isEditing && createdByCurrentUser && (
                                        <React.Fragment>
                                            <div className="form-group">
                                                <label className="col-form-label">My Rating/Review</label>

                                                <RatingFormInput
                                                    label={false}
                                                    value={noteModel.additional_data?.rating}
                                                    onChange={rating => setNoteModel({
                                                        ...noteModel,
                                                        additional_data: {rating}
                                                    })}
                                                />
                                                <textarea
                                                    value={noteModel.content}
                                                    onChange={e => setNoteModel({
                                                        ...noteModel,
                                                        content: e.target.value
                                                    })}
                                                    name="content" className="form-control" rows="3"
                                                    placeholder="Add you own notes here..."></textarea>
                                            </div>
                                            <div className="flex flex-row items-center">
                                                <button
                                                    onClick={e => {
                                                        setIsEditing(false)
                                                        setNoteModel({content: '', additional_data: {}})
                                                    }}
                                                    className="btn btn-outline-dark mx-2"
                                                    disabled={submitNoteForMeeting?.isLoading}>
                                                    Cancel
                                                </button>
                                                <button
                                                    onClick={e => {
                                                        submitNoteForMeeting.mutate();
                                                    }}
                                                    className="btn btn-primary mx-2"
                                                    disabled={submitNoteForMeeting?.isLoading || !noteModel.content?.length || !noteModel.additional_data?.rating}>
                                                    Submit
                                                </button>
                                            </div>
                                        </React.Fragment>
                                    )}

                                </div>

                            </div>
                        )
                    })}
                    {myRating === false && (
                        <React.Fragment>
                            <div className="form-group">
                                <label className="col-form-label">My Rating/Review</label>

                                <RatingFormInput
                                    label={false}
                                    value={noteModel.additional_data?.rating}
                                    onChange={rating => setNoteModel({...noteModel, additional_data: {rating}})}
                                />
                                <textarea
                                    value={noteModel.content}
                                    onChange={e => setNoteModel({...noteModel, content: e.target.value})}
                                    name="content" className="form-control" rows="3"
                                    placeholder="Add you own notes here..."></textarea>
                            </div>
                            <div className="flex flex-row items-center">
                                <button
                                    onClick={e => {
                                        setIsEditing(false)
                                        setNoteModel({content: '', additional_data: {}})
                                        onToggle()
                                    }}
                                    className="btn btn-outline-dark mx-2"
                                    disabled={submitNoteForMeeting?.isLoading}>
                                    Cancel
                                </button>
                                <button
                                    onClick={e => {
                                        submitNoteForMeeting.mutate();
                                    }}
                                    className="btn btn-primary mx-2"
                                    disabled={submitNoteForMeeting?.isLoading || !noteModel.content?.length || !noteModel.additional_data?.rating}>
                                    Submit
                                </button>
                            </div>
                        </React.Fragment>
                    )}
                </div>
            </ModalBody>
        </Modal>
    );
};

MeetingRatingsModal.propTypes = {
    meeting: PropTypes.any
};

export default MeetingRatingsModal;